import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import {
  AddIcon,
  CheckIcon,
  DeleteIcon,
  RefreshIcon,
  SendIcon,
} from '../components/icons';

/**
 * Slack integration — card spotlight-055, phase 1.
 *
 * Three states:
 *
 *   1. Not connected.  "Connect Slack" button → backend mints OAuth
 *      install URL + CSRF nonce → redirect to Slack consent screen.
 *   2. Mid-flow.  Slack redirects back with ?code= + ?state= ; we
 *      POST those to /oauth/callback to exchange for the bot token,
 *      then drop the params from the URL so a refresh doesn't replay.
 *   3. Connected.  Show workspace metadata, channel picker (paginated
 *      via cursor), "Send test message", "Disconnect".
 *
 * Phase 1 is one-way mirror only (Spotlight → Slack).  Phases 2 + 3
 * (reply-from-Slack, reactions, per-project routing) ship as
 * separate cards.
 */
export function Integrations() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [channels, setChannels] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmNode, ask] = useConfirm();
  // Card 061 phase 3 — per-project routing.  Loaded after the
  // integration is connected; routing.project_overrides is a list of
  // {project_id, channel_id, channel_name} the dashboard renders as
  // a row per project.  Projects without an override render the
  // default channel.
  const [routing, setRouting] = useState(null);
  const [projects, setProjects] = useState([]);
  const [routingSaving, setRoutingSaving] = useState(false);

  // OAuth callback runs once on mount when ?code= + ?state= are in the
  // URL.  Guard against React-strict-mode double-mount with a ref —
  // Slack rejects a replayed code with `code_already_used`.
  const callbackHandled = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api('/v1/admin/integrations/slack');
      setIntegration(resp || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // OAuth callback — Slack redirected here with code + state.
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code || !state) return;
    if (callbackHandled.current) return;
    callbackHandled.current = true;

    (async () => {
      setBusy(true);
      setError(null);
      try {
        const resp = await api('/v1/admin/integrations/slack/oauth/callback', {
          method: 'POST',
          body: JSON.stringify({ code, state }),
        });
        setIntegration(resp || null);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy(false);
        // Drop the OAuth params from the URL so a refresh doesn't
        // replay the code (Slack would reject anyway, but the
        // dashboard would render a confusing error toast).
        const next = new URLSearchParams(searchParams);
        next.delete('code');
        next.delete('state');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [api, searchParams, setSearchParams]);

  const connect = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = await api('/v1/admin/integrations/slack/install', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (resp?.install_url) {
        // Full-page redirect so Slack can drive the consent screen;
        // we come back to /integrations?code=…&state=…
        window.location.href = resp.install_url;
        return;
      }
      throw new Error('Slack returned no install URL');
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }, [api]);

  const disconnect = useCallback(async () => {
    const ok = await ask({
      title: 'Disconnect Slack?',
      body: 'Pinpoint threads will stop mirroring to Slack until you reconnect.',
      confirmLabel: 'Disconnect',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await api('/v1/admin/integrations/slack', { method: 'DELETE' });
      setIntegration(resp || null);
      setChannels([]);
      setNextCursor('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [api, ask]);

  const loadChannels = useCallback(async (cursor = '') => {
    setChannelsLoading(true);
    try {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
      const resp = await api(`/v1/admin/integrations/slack/channels${qs}`);
      setChannels((prev) => cursor ? [...prev, ...(resp?.channels || [])] : (resp?.channels || []));
      setNextCursor(resp?.next_cursor || '');
    } catch (e) {
      setError(e.message);
    } finally {
      setChannelsLoading(false);
    }
  }, [api]);

  // Pre-load the first page of channels once we know we're connected.
  useEffect(() => {
    if (integration?.connected && channels.length === 0) {
      loadChannels('');
    }
  }, [integration?.connected, channels.length, loadChannels]);

  const pickChannel = useCallback(async (channel) => {
    setBusy(true);
    setError(null);
    try {
      const resp = await api('/v1/admin/integrations/slack/channel', {
        method: 'POST',
        body: JSON.stringify({
          channel_id: channel.id,
          channel_name: channel.name,
        }),
      });
      setIntegration(resp || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [api]);

  // Per-project routing — load once we know the integration is
  // connected.  The route returns a stub (empty default + empty
  // overrides) when the tenant isn't connected, which we shouldn't
  // reach because the section only renders behind the connected
  // gate.
  const loadRouting = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        api('/v1/admin/integrations/slack/routing'),
        api('/v1/admin/projects'),
      ]);
      setRouting(r || { default_channel_id: '', project_overrides: [] });
      // The projects endpoint returns {projects: [...]} or [...] —
      // accept both shapes so a contract drift doesn't blank the
      // section.
      setProjects(Array.isArray(p) ? p : (p?.projects || p?.items || []));
    } catch (e) {
      setError(e.message);
    }
  }, [api]);

  useEffect(() => {
    if (integration?.connected && routing === null) {
      loadRouting();
    }
  }, [integration?.connected, routing, loadRouting]);

  const setProjectChannel = useCallback(async (projectId, channel) => {
    setRoutingSaving(true);
    setError(null);
    try {
      // Replace-not-merge for this one project.  Empty channel
      // deletes the override; the dashboard then renders "Default
      // channel" for that project row.
      const override = channel
        ? { project_id: projectId, channel_id: channel.id, channel_name: channel.name }
        : { project_id: projectId, channel_id: '', channel_name: '' };
      const resp = await api('/v1/admin/integrations/slack/channels', {
        method: 'PATCH',
        body: JSON.stringify({ project_overrides: [override] }),
      });
      setRouting(resp || routing);
    } catch (e) {
      setError(e.message);
    } finally {
      setRoutingSaving(false);
    }
  }, [api, routing]);

  const sendTest = useCallback(async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const resp = await api('/v1/admin/integrations/slack/test', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setTestResult(resp);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [api]);

  return (
    <section className="stack" style={{ marginTop: 4 }}>
      <div>
        <h2 style={{ margin: '0 0 4px' }}>Integrations</h2>
        <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13.5 }}>
          Mirror Pinpoint help threads + operator replies into your
          team's Slack workspace. Phase 1 is one-way (Spotlight →
          Slack); reply-from-Slack lands in a follow-up.
        </p>
      </div>

      {error ? (
        <div className="card" style={{ borderColor: 'var(--danger)', background: 'var(--danger-soft)', padding: 12 }}>
          <strong>Something went wrong:</strong> {error}
        </div>
      ) : null}

      <section className="card" style={{ padding: 16 }}>
        <h3 className="card__title" style={{ marginBottom: 8 }}>Slack</h3>

        {loading ? (
          <p style={{ color: 'var(--text-3)' }}>Loading…</p>
        ) : !integration?.connected ? (
          <div className="stack">
            <p style={{ color: 'var(--text-2)', fontSize: 13.5 }}>
              Pinpoint will open a new Slack thread for every help
              request, in the channel you pick. Operator replies in
              the dashboard mirror back to that thread.
            </p>
            <div>
              <button
                type="button"
                className="btn btn--primary"
                onClick={connect}
                disabled={busy}
                data-testid="slack-connect"
              >
                <AddIcon /> Connect Slack
              </button>
            </div>
          </div>
        ) : (
          <div className="stack">
            <div className="row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CheckIcon style={{ color: 'var(--success)' }} />
              <div>
                <div><strong>{integration.workspace_name || integration.workspace_id}</strong></div>
                <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>
                  {integration.workspace_domain ? `${integration.workspace_domain}.slack.com` : 'Connected'}
                </div>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={disconnect}
                  disabled={busy}
                  data-testid="slack-disconnect"
                >
                  <DeleteIcon /> Disconnect
                </button>
              </div>
            </div>

            <div className="stack">
              <h4 style={{ margin: '8px 0 4px' }}>Default channel</h4>
              <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13 }}>
                The bot must already be a member of the channel.
                Run <code>/invite @Spotlight</code> in Slack if it's
                missing here.
              </p>
              {integration.default_channel_id ? (
                <p style={{ color: 'var(--text-2)', fontSize: 13.5 }}>
                  Currently posting to <code>#{integration.default_channel_name || integration.default_channel_id}</code>.
                </p>
              ) : (
                <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
                  No channel selected yet — Pinpoint events won't
                  mirror until you pick one.
                </p>
              )}

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
                {channelsLoading && channels.length === 0 ? (
                  <li style={{ padding: 10, color: 'var(--text-3)' }}>Loading channels…</li>
                ) : null}
                {channels.map((c) => (
                  <li key={c.id} style={{ borderBottom: '1px solid var(--border)', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ flex: 1 }}>
                      #{c.name}{c.is_private ? <span style={{ color: 'var(--text-3)', marginLeft: 4 }}>(private)</span> : null}
                    </span>
                    {integration.default_channel_id === c.id ? (
                      <span style={{ color: 'var(--success)' }}><CheckIcon /></span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => pickChannel(c)}
                        disabled={busy}
                        data-testid={`slack-pick-${c.id}`}
                      >
                        Select
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => loadChannels('')}
                  disabled={channelsLoading}
                >
                  <RefreshIcon /> Refresh
                </button>
                {nextCursor ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => loadChannels(nextCursor)}
                    disabled={channelsLoading}
                  >
                    Load more
                  </button>
                ) : null}
              </div>
            </div>

            <div className="stack">
              <h4 style={{ margin: '8px 0 4px' }}>Routing</h4>
              <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13 }}>
                Route Pinpoint threads to a different channel per project.
                Anything without an override lands in the default channel
                above. Card 061 phase 3.
              </p>
              {routing === null ? (
                <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading routing…</p>
              ) : projects.length === 0 ? (
                <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
                  No projects yet — create one under Projects to set
                  per-project routing.
                </p>
              ) : (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-md)',
                  }}
                  data-testid="slack-routing-list"
                >
                  {projects.map((project) => {
                    const projectId = project.project_id || project.id;
                    const projectName = project.name || project.display_name || projectId;
                    const override = (routing?.project_overrides || []).find(
                      (o) => o.project_id === projectId,
                    );
                    return (
                      <li
                        key={projectId}
                        style={{
                          borderBottom: '1px solid var(--border)',
                          padding: '8px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <span style={{ flex: 1 }}>
                          <strong>{projectName}</strong>
                          <span style={{ color: 'var(--text-3)', marginLeft: 8, fontSize: 12 }}>
                            {override
                              ? `→ #${override.channel_name || override.channel_id}`
                              : '→ default channel'}
                          </span>
                        </span>
                        <select
                          value={override?.channel_id || ''}
                          onChange={(event) => {
                            const selectedId = event.target.value;
                            if (!selectedId) {
                              setProjectChannel(projectId, null);
                              return;
                            }
                            const chosen = channels.find((c) => c.id === selectedId);
                            if (chosen) setProjectChannel(projectId, chosen);
                          }}
                          disabled={routingSaving || channels.length === 0}
                          data-testid={`slack-routing-${projectId}`}
                        >
                          <option value="">Use default channel</option>
                          {channels.map((c) => (
                            <option key={c.id} value={c.id}>
                              #{c.name}
                            </option>
                          ))}
                        </select>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="stack">
              <h4 style={{ margin: '8px 0 4px' }}>Test message</h4>
              <p style={{ color: 'var(--text-3)', margin: 0, fontSize: 13 }}>
                Posts a one-off "Spotlight is wired up" message to
                the selected channel so you can confirm the bot has
                permission to post.
              </p>
              <div>
                <button
                  type="button"
                  className="btn"
                  onClick={sendTest}
                  disabled={busy || !integration.default_channel_id}
                  data-testid="slack-test"
                >
                  <SendIcon /> Send test message
                </button>
              </div>
              {testResult ? (
                <p style={{
                  color: testResult.ok ? 'var(--success)' : 'var(--danger)',
                  fontSize: 13.5,
                  margin: 0,
                }}>
                  {testResult.ok ? 'Test posted.' : `Failed: ${testResult.detail || 'unknown error'}`}
                </p>
              ) : null}
            </div>
          </div>
        )}
      </section>

      {confirmNode}
    </section>
  );
}
